import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  Clock3,
  FileSearch,
  FileText,
  Loader2,
  RefreshCw,
  ShieldAlert,
  Trash2,
  User,
  XCircle,
} from 'lucide-react';

import PermissionGate from '../components/PermissionGate';
import {
  EmptyState,
  InlineAlert,
  MetricCard,
  PageHeader,
  StatusPill,
  cx,
  formatDateTime,
} from '../components/EnterpriseAdminUI';
import { revocationAPI } from '../services/api';
import { normalizeRunStatus } from '../utils/revocationEvidence.mjs';

function runErrors(run) {
  const unique = new Map();
  const candidates = [
    ...(Array.isArray(run?.error_messages) ? run.error_messages : []),
    ...(Array.isArray(run?.executions)
      ? run.executions
          .filter((execution) => execution?.last_error)
          .map((execution) => ({
            request_id: execution.request_id,
            execution_id: execution.id,
            stage: 'revocation_execution',
            exception_type: 'RevocationExecutionFailure',
            message: execution.last_error,
          }))
      : []),
  ];

  candidates.forEach((error) => {
    const message = typeof error === 'string' ? error : error?.message || '';
    const requestId = typeof error === 'object' ? error?.request_id || '' : '';
    const key = `${requestId}|${message}`;
    if (!unique.has(key)) unique.set(key, error);
  });
  return [...unique.values()];
}

function errorText(error) {
  if (typeof error === 'string') return error;
  return [
    error?.stage && `Stage: ${error.stage}`,
    error?.request_id && `Request: ${error.request_id}`,
    error?.exception_type && `Type: ${error.exception_type}`,
    error?.message && `Error: ${error.message}`,
    error?.occurred_at && `Time: ${error.occurred_at}`,
  ].filter(Boolean).join('\n');
}

function RunStatus({ status, noWork = false }) {
  if (noWork) return <StatusPill label="No pending work" tone="neutral" icon={CheckCircle2} />;
  const normalized = normalizeRunStatus(status);
  const config = {
    success: { label: 'Completed', tone: 'success', icon: CheckCircle2 },
    failed: { label: 'Needs attention', tone: 'danger', icon: XCircle },
    running: { label: 'Running', tone: 'info', icon: RefreshCw },
    pending: { label: 'Queued', tone: 'warning', icon: Clock3 },
  }[normalized] || { label: status || 'Unknown', tone: 'neutral', icon: Clock3 };
  return <StatusPill {...config} />;
}

function ExecutionStatus({ execution }) {
  const status = String(execution?.status || '').toUpperCase();
  if (status === 'COMPLETED_DELETED') {
    return <StatusPill label="Revoked · deleted" tone="success" icon={Trash2} />;
  }
  if (status === 'COMPLETED_WITH_RETENTION') {
    return <StatusPill label="Revoked · retained data" tone="warning" icon={ShieldAlert} />;
  }
  if (['RETRY_PENDING', 'FAILED'].includes(status)) {
    return <StatusPill label="Retry required" tone="danger" icon={XCircle} />;
  }
  if (status === 'MANUAL_REVIEW') {
    return <StatusPill label="Manual review" tone="warning" icon={AlertTriangle} />;
  }
  return <StatusPill label={status.replaceAll('_', ' ') || 'Processing'} tone="info" icon={RefreshCw} />;
}

function itemGroup(items, bucket) {
  return (Array.isArray(items) ? items : []).filter((item) => item.bucket === bucket);
}

function FriendlyItem({ item, hold, onCreateHold, onReleaseHold }) {
  const retained = item.bucket === 'retained';
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <p className="font-bold text-slate-950">{item.label || 'Data item'}</p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            {item.resource_type === 'pii_data_item' && (
              <span className="rounded-full bg-blue-50 px-2 py-1 font-bold text-blue-700">PII field</span>
            )}
            {item.resource_type === 'document' && (
              <span className="rounded-full bg-violet-50 px-2 py-1 font-bold text-violet-700">Document</span>
            )}
            {item.bucket === 'deleted' && (
              <span className="rounded-full bg-emerald-50 px-2 py-1 font-bold text-emerald-700">Deleted</span>
            )}
            {retained && (
              <span className="rounded-full bg-amber-50 px-2 py-1 font-bold text-amber-800">Retained · restricted</span>
            )}
            {item.legal_hold && (
              <span className="rounded-full bg-red-50 px-2 py-1 font-bold text-red-700">Legal hold</span>
            )}
          </div>
          {retained && (
            <div className="mt-3 space-y-1 text-xs text-slate-600">
              <p><b>Reason:</b> {(item.reason_codes || []).join(', ').replaceAll('_', ' ') || 'Retention policy'}</p>
              <p><b>Retained until:</b> {formatDateTime(item.expires_at)}</p>
            </div>
          )}
          {item.last_error && <p className="mt-3 text-sm text-red-700">{item.last_error}</p>}
        </div>
        {retained && (
          <div className="shrink-0">
            {hold ? (
              <button
                type="button"
                onClick={() => onReleaseHold(hold)}
                className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-bold text-amber-900"
              >
                Release legal hold
              </button>
            ) : (
              <button
                type="button"
                onClick={() => onCreateHold(item)}
                className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs font-bold text-slate-700"
              >
                Place legal hold
              </button>
            )}
          </div>
        )}
      </div>
      <details className="mt-3 border-t border-slate-100 pt-3">
        <summary className="flex cursor-pointer items-center gap-2 text-xs font-bold text-slate-500">
          <ChevronDown className="h-3.5 w-3.5" /> Technical evidence
        </summary>
        <dl className="mt-3 grid gap-2 text-xs text-slate-600 sm:grid-cols-2">
          <div><dt className="font-bold">Resource ID</dt><dd className="break-all font-mono">{item.resource_id || '—'}</dd></div>
          <div><dt className="font-bold">Target</dt><dd className="break-all font-mono">{item.target_key || '—'}</dd></div>
          <div><dt className="font-bold">Attempts</dt><dd>{item.attempt_count || 0}</dd></div>
          <div><dt className="font-bold">Completed</dt><dd>{formatDateTime(item.completed_at)}</dd></div>
          <div className="sm:col-span-2"><dt className="font-bold">Evidence hash</dt><dd className="break-all font-mono">{item.evidence_hash || 'Pending'}</dd></div>
        </dl>
      </details>
    </div>
  );
}

function OutcomeSection({ title, description, items, tone, holds, onCreateHold, onReleaseHold }) {
  if (!items.length) return null;
  const toneClass = tone === 'success'
    ? 'border-emerald-200 bg-emerald-50/60'
    : tone === 'warning'
      ? 'border-amber-200 bg-amber-50/60'
      : 'border-red-200 bg-red-50/60';
  return (
    <section className={cx('rounded-3xl border p-4 sm:p-5', toneClass)}>
      <h4 className="font-black text-slate-950">{title}</h4>
      <p className="mt-1 text-sm text-slate-600">{description}</p>
      <div className="mt-4 grid gap-3 xl:grid-cols-2">
        {items.map((item) => (
          <FriendlyItem
            key={item.id}
            item={item}
            hold={holds.find((value) => String(value.item_id) === String(item.id) && value.status === 'ACTIVE')}
            onCreateHold={onCreateHold}
            onReleaseHold={onReleaseHold}
          />
        ))}
      </div>
    </section>
  );
}

function ExecutionCard({ execution, holds, onCreateHold, onReleaseHold }) {
  const record = execution.record || {};
  const deleted = itemGroup(execution.items, 'deleted');
  const retained = itemGroup(execution.items, 'retained');
  const attention = (execution.items || []).filter((item) => ['retry', 'manual_review', 'pending'].includes(item.bucket));
  const system = itemGroup(execution.items, 'system_evidence');

  return (
    <article className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-200 bg-slate-50/80 p-5 sm:p-6">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <ExecutionStatus execution={execution} />
              {execution.report_acknowledged
                ? <StatusPill label="Consent Platform confirmed" tone="success" icon={CheckCircle2} />
                : <StatusPill label="Confirmation pending" tone="warning" icon={Clock3} />}
            </div>
            <h3 className="mt-4 text-lg font-black text-slate-950">{record.notice_title || 'Consent record'}</h3>
            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-600">
              <span className="inline-flex items-center gap-1.5"><User className="h-4 w-4" /> {record.user_name || 'Unknown user'}</span>
              {record.user_email && <span>{record.user_email}</span>}
              {record.employee_id && <span>Employee ID: <b>{record.employee_id}</b></span>}
              <span>Submitted: <b>{formatDateTime(record.submitted_at)}</b></span>
            </div>
          </div>
          <div className="grid min-w-[320px] grid-cols-3 gap-2 text-center">
            <div className="rounded-2xl border bg-white p-3"><p className="text-xs text-slate-500">Deleted</p><p className="mt-1 text-xl font-black text-emerald-700">{deleted.length}</p></div>
            <div className="rounded-2xl border bg-white p-3"><p className="text-xs text-slate-500">Retained</p><p className="mt-1 text-xl font-black text-amber-700">{retained.length}</p></div>
            <div className="rounded-2xl border bg-white p-3"><p className="text-xs text-slate-500">Needs action</p><p className="mt-1 text-xl font-black text-red-700">{attention.length}</p></div>
          </div>
        </div>
      </div>

      <div className="space-y-4 p-5 sm:p-6">
        <OutcomeSection
          title="Successfully deleted"
          description="These submitted fields or documents were deleted and are no longer available for normal access."
          items={deleted}
          tone="success"
          holds={holds}
          onCreateHold={onCreateHold}
          onReleaseHold={onReleaseHold}
        />
        <OutcomeSection
          title="Retained with governed access"
          description="These items remain encrypted for the stated retention reason. Any access is exceptional, purpose-bound, and audited."
          items={retained}
          tone="warning"
          holds={holds}
          onCreateHold={onCreateHold}
          onReleaseHold={onReleaseHold}
        />
        <OutcomeSection
          title="Needs attention"
          description="These items were not treated as successfully completed and require retry or review."
          items={attention}
          tone="danger"
          holds={holds}
          onCreateHold={onCreateHold}
          onReleaseHold={onReleaseHold}
        />
        {system.length > 0 && (
          <details className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <summary className="cursor-pointer text-sm font-bold text-slate-700">System and audit evidence ({system.length})</summary>
            <div className="mt-3 grid gap-3 xl:grid-cols-2">
              {system.map((item) => <FriendlyItem key={item.id} item={item} holds={holds} onCreateHold={onCreateHold} onReleaseHold={onReleaseHold} />)}
            </div>
          </details>
        )}
        <details className="rounded-2xl border border-slate-200 p-4 text-xs text-slate-500">
          <summary className="cursor-pointer font-bold">Record identifiers</summary>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            <p><b>PII record:</b> <span className="break-all font-mono">{record.record_uuid || '—'}</span></p>
            <p><b>Consent:</b> <span className="break-all font-mono">{execution.consent_id || '—'}</span></p>
            <p><b>Revocation request:</b> <span className="break-all font-mono">{execution.request_id || '—'}</span></p>
            <p><b>Execution:</b> <span className="break-all font-mono">{execution.id || '—'}</span></p>
          </div>
        </details>
      </div>
    </article>
  );
}

export default function RevocationRunDetailPage() {
  const { id } = useParams();
  const [run, setRun] = useState(null);
  const [holds, setHolds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [actionMessage, setActionMessage] = useState('');

  const loadRun = useCallback(async ({ background = false } = {}) => {
    background ? setRefreshing(true) : setLoading(true);
    setError('');
    try {
      const [runResponse, holdsResponse] = await Promise.all([
        revocationAPI.getRun(id),
        revocationAPI.listLegalHolds().catch(() => ({ data: { results: [] } })),
      ]);
      setRun(runResponse.data || null);
      setHolds(Array.isArray(holdsResponse.data?.results) ? holdsResponse.data.results : []);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.response?.data?.error || err?.message || 'Failed to load revocation run.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [id]);

  useEffect(() => { loadRun(); }, [loadRun]);

  const active = ['running', 'pending'].includes(normalizeRunStatus(run?.status));
  useEffect(() => {
    if (!active) return undefined;
    const timer = window.setInterval(() => loadRun({ background: true }), 5000);
    return () => window.clearInterval(timer);
  }, [active, loadRun]);

  const errors = useMemo(() => runErrors(run), [run]);
  const executions = Array.isArray(run?.executions) ? run.executions : [];
  const totals = useMemo(() => executions.reduce((summary, execution) => {
    const counts = execution.summary || {};
    summary.deleted += Number(counts.deleted || 0);
    summary.retained += Number(counts.retained || 0);
    summary.attention += Number(counts.retry || 0) + Number(counts.manual_review || 0) + Number(counts.pending || 0);
    if (execution.status === 'COMPLETED_DELETED') summary.fullyDeleted += 1;
    if (execution.status === 'COMPLETED_WITH_RETENTION') summary.withRetention += 1;
    return summary;
  }, { deleted: 0, retained: 0, attention: 0, fullyDeleted: 0, withRetention: 0 }), [executions]);

  const createHold = async (item) => {
    const holdReference = window.prompt('Legal hold reference');
    if (!holdReference) return;
    const reason = window.prompt('Reason for the legal hold') || '';
    try {
      await revocationAPI.createLegalHold({ item_id: item.id, hold_reference: holdReference, reason });
      setActionMessage('Legal hold created.');
      await loadRun({ background: true });
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to create legal hold.');
    }
  };

  const releaseHold = async (hold) => {
    const reason = window.prompt('Reason for releasing this legal hold');
    if (reason === null) return;
    try {
      await revocationAPI.releaseLegalHold(hold.id, reason);
      setActionMessage('Legal hold released.');
      await loadRun({ background: true });
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to release legal hold.');
    }
  };

  if (loading) {
    return <div className="flex min-h-[60vh] items-center justify-center"><div className="flex items-center gap-3 rounded-2xl border bg-white px-6 py-5 shadow-sm"><Loader2 className="h-5 w-5 animate-spin text-orange-600" /><span className="text-sm font-bold text-slate-700">Loading revocation run…</span></div></div>;
  }

  return (
    <PermissionGate permission="view_retention_dashboard" showFallback>
      <div className="space-y-6">
        <PageHeader
          eyebrow="Retention operations"
          title="Revocation run details"
          description="Review each affected submission in business terms, with technical evidence available only when needed."
          icon={FileSearch}
          actions={<><Link to="/revocations/runs" className="inline-flex items-center gap-2 rounded-xl border bg-white px-4 py-2.5 text-sm font-bold text-slate-700"><ArrowLeft className="h-4 w-4" />Back to runs</Link><button type="button" onClick={() => loadRun({ background: true })} disabled={refreshing} className="inline-flex items-center gap-2 rounded-xl border bg-white px-4 py-2.5 text-sm font-bold text-slate-700 disabled:opacity-60"><RefreshCw className={cx('h-4 w-4', refreshing && 'animate-spin')} />Refresh</button></>}
        />

        {error && <InlineAlert tone="danger" title="Unable to complete the operation">{error}</InlineAlert>}
        {actionMessage && <InlineAlert tone="success" title="Retention state updated">{actionMessage}</InlineAlert>}

        {!run ? <EmptyState icon={AlertTriangle} title="Run not available" description="The run may not exist in this tenant." /> : (
          <>
            <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <RunStatus status={run.status} noWork={run.no_work} />
                    <StatusPill label={run.trigger_source === 'MANUAL' ? 'Manual run' : 'Scheduled run'} tone="info" />
                    {active && <StatusPill label="Auto-refreshing" tone="info" icon={RefreshCw} />}
                  </div>
                  <div className="mt-4 grid gap-2 text-sm text-slate-600 sm:grid-cols-2 xl:grid-cols-4">
                    <p><b>Started:</b> {formatDateTime(run.started_at)}</p>
                    <p><b>Finished:</b> {formatDateTime(run.finished_at)}</p>
                    <p><b>Triggered by:</b> {run.triggered_by_email || (run.trigger_source === 'MANUAL' ? 'Administrator' : 'Scheduler')}</p>
                    <p><b>Run ID:</b> <span className="font-mono text-xs">{run.id}</span></p>
                  </div>
                </div>
                <InlineAlert tone={errors.length || totals.attention ? 'warning' : 'success'} title="Operational result">
                  {run.no_work
                    ? 'The manual check completed and found no pending revocation requests.'
                    : errors.length
                      ? 'One or more records require attention. Review the error section below.'
                      : totals.attention
                        ? 'Some records still require retry or manual review.'
                        : active
                          ? 'The worker is processing the affected submissions.'
                          : 'All affected submissions reached an accounted outcome.'}
                </InlineAlert>
              </div>
            </section>

            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
              <MetricCard label="Submissions processed" value={executions.length} hint="One card per affected record" icon={FileText} tone="info" />
              <MetricCard label="Fully deleted records" value={totals.fullyDeleted} hint="No retained personal data" icon={Trash2} tone="success" />
              <MetricCard label="Records with retention" value={totals.withRetention} hint="Governed retained data" icon={ShieldAlert} tone={totals.withRetention ? 'warning' : 'neutral'} />
              <MetricCard label="Items needing action" value={totals.attention} hint="Retry or review" icon={XCircle} tone={totals.attention ? 'danger' : 'neutral'} />
              <MetricCard label="Worker errors" value={errors.length} hint="Deduplicated" icon={AlertTriangle} tone={errors.length ? 'danger' : 'neutral'} />
            </div>

            {executions.length ? (
              <div className="space-y-5">
                {executions.map((execution) => <ExecutionCard key={execution.id} execution={execution} holds={holds} onCreateHold={createHold} onReleaseHold={releaseHold} />)}
              </div>
            ) : (
              <EmptyState icon={CheckCircle2} title={run.no_work ? 'No pending revocations' : 'No execution records'} description={run.no_work ? 'The platform is already up to date.' : 'No affected submissions were attached to this run.'} />
            )}

            {errors.length > 0 && (
              <section className="overflow-hidden rounded-3xl border border-red-200 bg-white shadow-sm">
                <div className="border-b border-red-100 bg-red-50 p-5"><h2 className="font-black text-red-900">Errors requiring attention</h2><p className="mt-1 text-sm text-red-700">Technical details are kept here rather than mixed with completed record outcomes.</p></div>
                <div className="divide-y divide-red-100">
                  {errors.map((item, index) => <pre key={index} className="whitespace-pre-wrap break-words p-5 font-mono text-sm leading-6 text-red-800">{errorText(item)}</pre>)}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </PermissionGate>
  );
}
